import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import LogisticsNotice from "@/components/LogisticsNotice";

/**
 * Polls /api/payments/stripe/status/{session_id} every 2s (max ~40s) until
 * payment_status flips to "paid" or "failed"/"expired". Redirects to the
 * appropriate follow-on page on success.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, error: "" });
  const tries = useRef(0);

  useEffect(() => {
    if (!session_id) {
      setState({ phase: "error", data: null, error: "No session id in URL." });
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/stripe/status/${session_id}`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", data, error: "" });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ phase: "failed", data, error: "" });
          return;
        }
        if (tries.current >= 20) {
          setState({ phase: "timeout", data, error: "" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({
          phase: "error", data: null,
          error: formatApiError(e.response?.data?.detail) || e.message,
        });
      }
    };
    poll();
    return () => { stopped = true; };
  }, [session_id]);

  const nextHref =
    state.data?.purpose === "product_order" && state.data?.product_id
      ? `/product/${state.data.product_id}`
      : state.data?.purpose === "subscription"
      ? "/billing"
      : state.data?.purpose === "boost"
      ? "/seller"
      : "/discover";

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md rounded-xl border border-[#27272A] bg-[#121212] p-8 text-center">
        {state.phase === "polling" && (
          <>
            <Loader2 className="h-10 w-10 text-[#007AFF] animate-spin mx-auto" />
            <h1 className="mt-6 font-black text-2xl tracking-tight">Confirming payment…</h1>
            <p className="text-neutral-400 text-sm mt-2">Waiting for Stripe to settle. Usually a few seconds.</p>
            <p className="text-xs text-neutral-500 font-mono mt-4">{session_id}</p>
          </>
        )}
        {state.phase === "success" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-[#34C759] mx-auto" />
            <h1 className="mt-4 font-black text-2xl tracking-tight">Payment confirmed</h1>
            <p className="text-neutral-400 text-sm mt-2">
              {state.data?.purpose === "subscription"
                ? "Your monthly access is active for another 30 days."
                : state.data?.purpose === "boost"
                ? "Your listing is boosted — it's now ranking higher in buyer feeds for the next 24 hours."
                : "Your order has been placed."}
            </p>
            <div className="mt-4 text-sm text-neutral-500 font-mono">
              USD ${state.data?.amount_usd?.toFixed(2)} via Stripe
            </div>
            {state.data?.purpose === "product_order" && (
              <div className="mt-4 text-left">
                <LogisticsNotice variant="buyer" />
              </div>
            )}
            <Button
              data-testid="stripe-success-continue"
              className="mt-8 bg-white text-black hover:bg-neutral-200 rounded-full w-full h-11 font-semibold"
              onClick={() => nav(nextHref)}
            >
              Continue
            </Button>
          </>
        )}
        {(state.phase === "failed" || state.phase === "timeout") && (
          <>
            <XCircle className="h-12 w-12 text-[#FF3B30] mx-auto" />
            <h1 className="mt-4 font-black text-2xl tracking-tight">
              {state.phase === "failed" ? "Payment failed" : "Still processing"}
            </h1>
            <p className="text-neutral-400 text-sm mt-2">
              {state.phase === "failed"
                ? "The card was declined or the session expired. Nothing was charged."
                : "Taking longer than expected — the webhook will finalize this in the background. Check /billing or your order shortly."}
            </p>
            <Link to="/billing">
              <Button className="mt-8 bg-white text-black hover:bg-neutral-200 rounded-full w-full h-11 font-semibold">
                Back
              </Button>
            </Link>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle className="h-12 w-12 text-[#FF3B30] mx-auto" />
            <h1 className="mt-4 font-black text-2xl tracking-tight">Error</h1>
            <p className="text-neutral-400 text-sm mt-2">{state.error}</p>
            <Link to="/billing">
              <Button className="mt-8 bg-white text-black hover:bg-neutral-200 rounded-full w-full h-11 font-semibold">
                Back to billing
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
